/**
 * Contracts — the part of the business that pays the wages in February.
 *
 * Installs are lumpy; agreements are not. So this screen is built around the
 * three things that quietly destroy the value of one:
 *
 *   1. **A visit nobody booked.** On a four-hour-response agreement a missed
 *      service is a breach, and it is found out about during an incident.
 *   2. **A renewal nobody noticed.** An agreement that rolls over at last
 *      year's price loses money every year by exactly inflation.
 *   3. **A margin nobody looked at.** `realised_margin_bp` has carried the
 *      comment since the first commit: a contract running at 29% is a
 *      repricing conversation, not a surprise at renewal.
 *
 * Which is why the list is sorted by what needs doing rather than by name.
 * A contracts screen in alphabetical order is one nobody reads past the first
 * screenful, and the rows that matter are never at the top of the alphabet.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { ApiError, api } from "../lib/api";
import { OFFICE, useAuth } from "../lib/auth";
import { day, money, moneyShort, percent, titleCase } from "../lib/format";
import { Card, Chip, DataTable, Empty, ErrorState, Loading, PageHeader, Tile, Tiles } from "../components/ui";
import type { BillingDueRow, ContractOut, ContractVisitOut, RaisedInvoice, RenewalPreview } from "../lib/types";

export default function Contracts() {
  const { can } = useAuth();
  const isOffice = can(...OFFICE);
  // Invoices are the owner's and the office's; a supervisor books visits.
  const canBill = can("owner", "office");
  const [open, setOpen] = useState<string | null>(null);
  // Lives here, not in the "To invoice" card: raising everything empties
  // that card, and the note saying where the invoices went went with it.
  const [raisedNote, setRaisedNote] = useState<{ tone: "info" | "crit"; text: React.ReactNode } | null>(null);

  const contracts = useQuery({ queryKey: ["contracts"], queryFn: () => api.contracts.list() });
  const due = useQuery({ queryKey: ["contracts", "due"], queryFn: () => api.contracts.due(30) });
  const billingDue = useQuery({
    queryKey: ["contracts", "billing-due"],
    queryFn: () => api.contracts.billingDue(),
    enabled: isOffice,
  });

  if (contracts.isPending) return <Loading what="Loading agreements" />;
  if (contracts.error) return <ErrorState error={contracts.error} onRetry={() => void contracts.refetch()} />;

  const rows = contracts.data;
  const annual = rows.reduce((sum, c) => sum + c.annual_value_cents, 0);
  const attention = rows.filter((c) => c.standing.needs_attention);
  const renewing = rows.filter((c) => c.standing.renewing_soon);
  const toBill = billingDue.data ?? [];
  const toBillCount = toBill.reduce((n, r) => n + r.due.length, 0);
  const toBillCents = toBill.reduce((n, r) => n + r.due_cents, 0);

  return (
    <div className="page">
      <PageHeader title="Contracts">
        {rows.length} live agreement{rows.length === 1 ? "" : "s"}. Sorted by what needs doing, not by name.
      </PageHeader>

      <Tiles>
        <Tile label="Under agreement" value={moneyShort(annual)} detail="a year, recurring" />
        <Tile
          label="Need attention"
          value={attention.length}
          detail={attention.length ? "overdue visits or slipping margin" : "nothing outstanding"}
          tone={attention.length ? "warn" : "ok"}
        />
        <Tile
          label="Renewing soon"
          value={renewing.length}
          detail={renewing.length ? "within 60 days" : "none in the next 60 days"}
          tone={renewing.length ? "info" : undefined}
        />
        {isOffice && (
          <Tile
            label="Invoices to raise"
            value={toBillCount}
            detail={toBillCount ? `${money(toBillCents)} ex GST, due now` : "every period is invoiced"}
            tone={toBillCount ? "warn" : undefined}
          />
        )}
      </Tiles>

      {canBill && <AutoRaise />}

      {raisedNote && (
        <p className={`notice notice-${raisedNote.tone}`} role="status">
          {raisedNote.text}
        </p>
      )}

      <div className="split">
        <div className="split-main">
          {rows.length === 0 ? (
            <Card title="Agreements">
              <Empty title="No maintenance agreements yet">
                Accepting a quote that includes a maintenance kit creates one, with its visit schedule
                already materialised.
              </Empty>
            </Card>
          ) : (
            rows.map((contract) => (
              <ContractCard
                key={contract.id}
                contract={contract}
                expanded={open === contract.id}
                isOffice={isOffice}
                canBill={canBill}
                onToggle={() => setOpen(open === contract.id ? null : contract.id)}
              />
            ))
          )}
        </div>

        <aside className="split-side">
          {isOffice && toBill.length > 0 && <BillingDue rows={toBill} canBill={canBill} onSaid={setRaisedNote} />}
          <Card
            title="Visits due"
            subtitle="Next 30 days, plus anything already late"
            padded={false}
          >
            {due.isPending ? (
              <Loading what="Loading the schedule" />
            ) : due.data && due.data.length > 0 ? (
              <DueList visits={due.data} />
            ) : (
              <Empty title="Nothing due">
                A visit that is late never drops off this list — being old does not make it less due.
              </Empty>
            )}
          </Card>
        </aside>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- card */

function ContractCard({
  contract,
  expanded,
  isOffice,
  canBill,
  onToggle,
}: {
  contract: ContractOut;
  expanded: boolean;
  isOffice: boolean;
  canBill: boolean;
  onToggle: () => void;
}) {
  const detail = useQuery({
    queryKey: ["contracts", contract.id],
    queryFn: () => api.contracts.get(contract.id),
    enabled: expanded,
  });

  const standing = contract.standing;
  const tone = standing.missed || standing.expired ? "crit" : standing.needs_attention ? "warn" : "ok";

  return (
    <Card
      title={contract.name}
      subtitle={`${contract.code} · ${contract.customer_name}${contract.site_name ? ` · ${contract.site_name}` : ""}`}
      actions={
        <Chip tone={tone} dot>
          {standing.missed
            ? `${standing.missed} missed`
            : standing.expired
              ? "Ended"
              : standing.overdue
                ? `${standing.overdue} overdue`
                : standing.renewing_soon
                  ? `Renews in ${standing.days_to_renewal}d`
                  : titleCase(contract.status)}
        </Chip>
      }
    >
      {standing.warnings.map((warning, i) => (
        <p key={i} className={`notice ${i === 0 && tone === "crit" ? "notice-crit" : "notice-warn"}`}>
          {warning}
        </p>
      ))}

      <dl className="kv kv-money">
        <dt>Annual value</dt>
        <dd className="mono">{money(contract.annual_value_cents)}</dd>
        <dt>Service</dt>
        <dd>
          {titleCase(contract.service_frequency)}
          {contract.response_sla_minutes ? ` · ${contract.response_sla_minutes / 60}h response` : ""}
        </dd>
        <dt>Term</dt>
        <dd>
          {day(contract.starts_on)} – {contract.ends_on ? day(contract.ends_on) : "open"}
        </dd>
        <dt>Realised margin</dt>
        <dd className="mono">
          {/* Three states, not two. Null margin with no jobs is "nothing has
              happened"; null margin *with* jobs is "work has been done and
              none of it is priced yet" — a different thing, and worth saying,
              because it is usually maintenance somebody forgot to invoice.
              Showing 0% for either would have somebody repricing an agreement
              that is working fine. */}
          {contract.job_count === 0 ? (
            <span className="muted">no jobs yet</span>
          ) : contract.realised_margin_bp === null ? (
            <span className="muted">
              {contract.job_count} job{contract.job_count === 1 ? "" : "s"}, none priced yet
            </span>
          ) : (
            <>
              <b className={contract.realised_margin_bp < 3100 ? "text-crit" : ""}>
                {percent(contract.realised_margin_bp)}
              </b>
              <span className="tiny muted">
                {" "}
                · {money(contract.revenue_to_date_cents)} over {contract.job_count} job
                {contract.job_count === 1 ? "" : "s"}
              </span>
            </>
          )}
        </dd>
        {isOffice && (
          <>
            <dt>Billing</dt>
            <dd>
              <Billing contract={contract} canBill={canBill} />
            </dd>
          </>
        )}
      </dl>

      <div className="btn-row" style={{ marginTop: 12 }}>
        <button className="btn" type="button" onClick={onToggle}>
          {expanded ? "Hide schedule" : `Schedule (${contract.visit_count})`}
        </button>
        {isOffice && <RenewButton contract={contract} />}
      </div>

      {expanded &&
        (detail.isPending ? (
          <Loading what="Loading the schedule" />
        ) : detail.data ? (
          <Schedule visits={detail.data.visits} contractId={contract.id} />
        ) : null)}
    </Card>
  );
}

/* --------------------------------------------------------------- schedule */

function Schedule({ visits, contractId }: { visits: ContractVisitOut[]; contractId: string }) {
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["contracts"] });
    void queryClient.invalidateQueries({ queryKey: ["contracts", contractId] });
  };

  const book = useMutation({
    mutationFn: (visitId: string) => api.contracts.book(visitId),
    onSuccess: invalidate,
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't book that visit."),
  });

  const complete = useMutation({
    mutationFn: (visitId: string) => api.contracts.complete(visitId),
    onSuccess: invalidate,
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't mark it done."),
  });

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div style={{ marginTop: 12 }}>
      {problem && (
        <p className="notice notice-crit" role="alert">
          {problem}
        </p>
      )}
      <DataTable
        columns={[
          { key: "due", label: "Due" },
          { key: "status", label: "Status" },
          { key: "job", label: "Job", hideOnPhone: true },
          { key: "do", label: "", align: "right" },
        ]}
      >
        {visits.map((visit) => {
          const late = visit.status !== "done" && !visit.completed_on && visit.due_on < today;
          return (
            <tr key={visit.id}>
              <td className={late ? "text-crit" : ""}>{day(visit.due_on)}</td>
              <td>
                <Chip tone={visit.status === "done" ? "ok" : late ? "crit" : visit.status === "booked" ? "info" : "neutral"}>
                  {visit.completed_on ? `Done ${day(visit.completed_on)}` : titleCase(visit.status)}
                </Chip>
              </td>
              <td className="phone-hide mono muted">{visit.job_number ?? "—"}</td>
              <td className="right">
                {visit.status === "done" ? null : visit.job_id ? (
                  <button
                    className="btn"
                    type="button"
                    onClick={() => complete.mutate(visit.id)}
                    disabled={complete.isPending}
                  >
                    Mark done
                  </button>
                ) : (
                  <button
                    className="btn"
                    type="button"
                    onClick={() => book.mutate(visit.id)}
                    disabled={book.isPending}
                  >
                    Book it
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </DataTable>
    </div>
  );
}

/* ---------------------------------------------------------------- renewal */

/**
 * Shown before it is done.
 *
 * The uplift is the number somebody has to justify to a customer, and it
 * should not first appear in their inbox.
 */
function RenewButton({ contract }: { contract: ContractOut }) {
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<RenewalPreview | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useMutation({
    mutationFn: () => api.contracts.previewRenewal(contract.id),
    onSuccess: setPreview,
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't work out the renewal."),
  });

  const renew = useMutation({
    mutationFn: () => api.contracts.renew(contract.id),
    onSuccess: () => {
      setPreview(null);
      void queryClient.invalidateQueries({ queryKey: ["contracts"] });
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't renew it."),
  });

  if (!preview) {
    return (
      <>
        <button className="btn" type="button" onClick={() => load.mutate()} disabled={load.isPending}>
          {load.isPending ? "Working it out…" : "Renew"}
        </button>
        {problem && (
          <p className="tiny" style={{ color: "var(--crit)" }} role="alert">
            {problem}
          </p>
        )}
      </>
    );
  }

  return (
    <div style={{ width: "100%", marginTop: 10 }}>
      <div className="notice notice-info">
        <b>
          {money(preview.previous_annual_value_cents)} → {money(preview.annual_value_cents)}
        </b>{" "}
        a year
        {preview.uplift_cents !== 0 && (
          <>
            {" "}
            ({percent(preview.uplift_bp, 1)} uplift, {money(preview.uplift_cents)})
          </>
        )}
        .<br />
        New term {day(preview.starts_on)} – {day(preview.ends_on)}, with {preview.visits.length} visit
        {preview.visits.length === 1 ? "" : "s"} scheduled.
        {/* An unchanged number with no explanation reads as a broken screen,
            and the reason it is unchanged is the thing worth saying out loud:
            this agreement rolls over at last year's price, which falls behind
            by exactly inflation and does so quietly. Saying it here puts the
            fact in front of the one person who can change it, at the one
            moment they are looking. */}
        {preview.uplift_bp === 0 && (
          <>
            <br />
            <span className="tiny">
              No uplift on this agreement, so it renews at last year's price.
            </span>
          </>
        )}
      </div>
      <div className="btn-row">
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => renew.mutate()}
          disabled={renew.isPending}
        >
          {renew.isPending ? "Renewing…" : "Renew at this price"}
        </button>
        <button className="btn" type="button" onClick={() => setPreview(null)}>
          Cancel
        </button>
      </div>
      {problem && (
        <p className="tiny" style={{ color: "var(--crit)" }} role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- due list */

function DueList({ visits }: { visits: ContractVisitOut[] }) {
  const today = new Date().toISOString().slice(0, 10);

  return (
    <DataTable
      columns={[
        { key: "due", label: "Due" },
        { key: "what", label: "Agreement" },
      ]}
    >
      {visits.map((visit) => (
        <tr key={visit.id}>
          <td className={visit.due_on < today ? "text-crit mono" : "mono"}>{day(visit.due_on)}</td>
          <td>
            {/* The name comes back on this list from the server — a date with
                nothing beside it is a row nobody can act on. */}
            {visit.contract_name ?? <span className="muted">—</span>}
            {visit.status === "booked" && (
              <>
                {" "}
                <Chip tone="info">Booked</Chip>
              </>
            )}
          </td>
        </tr>
      ))}
    </DataTable>
  );
}

/* ---------------------------------------------------------------- billing */

/** Where the agreement's invoicing is up to, and the buttons that move it (CONTRACT-2). */
function Billing({ contract, canBill }: { contract: ContractOut; canBill: boolean }) {
  const b = contract.billing;
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [from, setFrom] = useState(b.bill_from ?? b.next?.start ?? new Date().toISOString().slice(0, 10));
  const [said, setSaid] = useState<{ tone: "info" | "crit"; text: React.ReactNode } | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["contracts"] });
    void queryClient.invalidateQueries({ queryKey: ["invoices"] });
  };
  const fail = (err: unknown) =>
    setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Something went wrong." });

  const setUp = useMutation({
    mutationFn: (bill_from: string | null) => api.contracts.setBilling(contract.id, { bill_from }),
    onSuccess: () => {
      setEditing(false);
      setSaid(null);
      refresh();
    },
    onError: fail,
  });
  const raise = useMutation({
    mutationFn: () => api.contracts.raise(contract.id),
    onSuccess: (raised) => {
      refresh();
      setSaid({ tone: "info", text: raisedText(raised) });
    },
    onError: fail,
  });

  const every = b.frequency === "monthly" ? "a month" : b.frequency === "quarterly" ? "a quarter" : `per ${titleCase(b.frequency).toLowerCase()} period`;

  if (!b.bill_from || editing) {
    return (
      <div>
        {!b.bill_from && (
          <p className="tiny muted" style={{ margin: "0 0 6px" }}>
            Not invoiced by Keystone yet. Pick the first day Keystone should bill — for an agreement you were
            invoicing somewhere else, the day after the last period billed there.
          </p>
        )}
        {canBill ? (
          <div className="btn-row">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="Bill from" />
            <button
              className="btn btn-primary btn-sm"
              type="button"
              disabled={!from || setUp.isPending}
              onClick={() => setUp.mutate(from)}
            >
              {b.bill_from ? "Save" : "Start billing"}
            </button>
            {editing && (
              <>
                <button className="btn btn-sm" type="button" onClick={() => setUp.mutate(null)}>
                  Stop billing
                </button>
                <button className="btn btn-sm" type="button" onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </>
            )}
          </div>
        ) : (
          <span className="muted">Not set up</span>
        )}
        {said && <p className={`notice notice-${said.tone}`} style={{ marginTop: 6 }}>{said.text}</p>}
      </div>
    );
  }

  return (
    <div>
      <span className="mono">{money(b.per_period_cents)}</span> {every} + GST
      <span className="tiny muted">
        {" "}
        · from {day(b.bill_from)}
        {b.billed_to ? ` · billed to ${day(b.billed_to)}` : " · nothing billed yet"}
        {b.next ? ` · next ${day(b.next.start)}` : ""}
      </span>
      {b.due.length > 0 && (
        <div className="notice notice-warn" style={{ marginTop: 6 }}>
          {b.due.length === 1 ? "One invoice" : `${b.due.length} invoices`} due: {b.due.map((p) => p.label).join(", ")} —{" "}
          {money(b.due_cents)} ex GST.
        </div>
      )}
      {canBill && (
        <div className="btn-row" style={{ marginTop: 6 }}>
          {b.due.length > 0 && (
            <button className="btn btn-primary btn-sm" type="button" disabled={raise.isPending} onClick={() => raise.mutate()}>
              {raise.isPending ? "Raising…" : `Raise ${b.due.length === 1 ? "it" : `all ${b.due.length}`}`}
            </button>
          )}
          <button className="btn btn-sm" type="button" onClick={() => setEditing(true)}>
            Change
          </button>
        </div>
      )}
      {said && <p className={`notice notice-${said.tone}`} style={{ marginTop: 6 }}>{said.text}</p>}
    </div>
  );
}

function raisedText(raised: RaisedInvoice[]): React.ReactNode {
  if (raised.length === 0) return "Nothing to raise — every period is already invoiced.";
  return (
    <>
      Raised {raised.map((r) => r.number).join(", ")}. They're waiting in{" "}
      <Link to="/invoices?show=unsent">Invoices → Not sent</Link> — check them and send; Xero hears about each one
      once it's sent.
    </>
  );
}

/** Across every agreement: what should be invoiced by now. */
function BillingDue({
  rows,
  canBill,
  onSaid: setSaid,
}: {
  rows: BillingDueRow[];
  canBill: boolean;
  onSaid: (said: { tone: "info" | "crit"; text: React.ReactNode }) => void;
}) {
  const queryClient = useQueryClient();
  const count = rows.reduce((n, r) => n + r.due.length, 0);
  const all = useMutation({
    mutationFn: () => api.contracts.raiseAll(),
    onSuccess: (raised) => {
      void queryClient.invalidateQueries({ queryKey: ["contracts"] });
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      setSaid({ tone: "info", text: raisedText(raised) });
    },
    onError: (err) => setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Couldn't raise them." }),
  });

  return (
    <Card title="To invoice" subtitle="Agreement periods due now, billed in advance">
      <div className="stack">
        {rows.map((r) => (
          <div key={r.contract_id} className="tiny">
            <b>{r.code}</b> · {r.customer_name}
            <div className="muted">
              {r.due.map((p) => p.label).join(", ")} — <span className="mono">{money(r.due_cents)}</span> ex GST
            </div>
          </div>
        ))}
      </div>
      {canBill && (
        <div className="btn-row" style={{ marginTop: 10 }}>
          <button className="btn btn-primary" type="button" disabled={all.isPending} onClick={() => all.mutate()}>
            {all.isPending ? "Raising…" : `Raise ${count === 1 ? "it" : `all ${count}`}`}
          </button>
        </div>
      )}
    </Card>
  );
}

/** CONTRACT-3: raise what is due each morning, without anyone pressing Raise. */
function AutoRaise() {
  const queryClient = useQueryClient();
  const org = useQuery({ queryKey: ["org"], queryFn: () => api.org.get() });
  const save = useMutation({
    mutationFn: (on: boolean) => api.org.update({ auto_raise_contract_invoices: on }),
    onSuccess: (next) => queryClient.setQueryData(["org"], next),
  });
  if (!org.data) return null;
  const on = org.data.auto_raise_contract_invoices;
  return (
    <label className="tiny" style={{ display: "flex", gap: 8, alignItems: "center", margin: "0 0 12px" }}>
      <input
        type="checkbox"
        checked={on}
        disabled={save.isPending}
        onChange={(e) => save.mutate(e.target.checked)}
      />
      <span>
        Raise agreement invoices automatically each morning.{" "}
        <span className="muted">
          They still wait in Invoices → Not sent for you to check and send — nothing reaches a customer or Xero on
          its own.
        </span>
      </span>
    </label>
  );
}
